<template>
  <div id="app">
    <template v-if="screenIDInactive">
      <DeviceActivation :screenID="screenID"/>
    </template>
    <!-- <template v-else-if="showSlideshows">
      <Slideshow v-bind="slideshows"/>
    </template> -->
    <template v-else-if="!loading">
      <div class="section header-container">
        <Header/>
      </div>
      <div class="section app-body">
        <Body :bookings="bookings"/>
      </div>
      <footer>
        <Footer/>
      </footer>
    </template>
    <vue-snotify></vue-snotify>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import moment from 'moment';

import { getSlideshows } from './services/endpoints';
import Body from './components/Body';
import Footer from './components/Footer';
import Header from './components/Header';
import Slideshow from './components/Slideshow';
import DeviceActivation from './components/DeviceActivation';
import CapAlertModal from './components/CAPAlert/Modal';
import NotificationMixin from './mixins/notification-mixin';
import ModalMixins from './mixins/modal-mixins';
import {setBSHWLED} from './mixins/booking-mixins';

export default {
  name: 'App',
  mixins: [NotificationMixin, ModalMixins],

  components: {
    Body,
    Header,
    Footer,
    Slideshow,
    DeviceActivation,
  },

  created() {
    // Capture and Validate screen ID before components are loaded
    // const screenID = this.getScreenID();
    // Temporary screen ID for testing
    const screenID = 'DED5EC420690'

    screenID !== null
      ? this.checkScreenActivated(screenID)
      : ((this.screenIDInactive = true),
      (this.screenID = 'No Screen ID found'));
  },

  beforeDestroy() {
    if (this.checkAvailabilityInterval) {
      clearInterval(this.checkAvailabilityInterval);
      this.checkAvailabilityInterval = null;
    }

    if (this.checkScreenIDInterval) {
      clearInterval(this.checkScreenIDInterval);
      this.checkScreenIDInterval = null;
    }

    if (this.checkCapAlertInterval) {
      clearInterval(this.checkCapAlertInterval);
      this.checkCapAlertInterval = null;
    }

    if (this.refreshEventsInterval) {
      clearInterval(this.refreshEventsInterval);
      this.refreshEventsInterval = null;
    }
  },

  data: () => ({
    screenID: null,
    loading: true,
    screenIDInactive: false,
    checkAvailabilityInterval: null,
    checkCapAlertInterval: null,
    checkScreenIDInterval: null,
    refreshEventsInterval: null,
    showSlideshows: false,
    timeFormat: 'hh:mm:ss',
    slideshows: [],
  }),

  computed: {
    ...mapState({
      device: 'device',
      bookings: 'bookings',
      capAlertURLs: 'capAlertURLs',
      capAlerts: state => state.capAlerts.alert,
      selectedDate: 'selectedDate',
      spaceId: state => state.space.id,
      isOpenAvailability: state =>
        state.device.space.availiblity_start === null ||
        state.device.space.availiblity_end === null,
    }),

    availabilityStart() {
      return moment(this.device.space.availiblity_start, this.timeFormat);
    },

    availabilityEnd() {
      return moment(this.device.space.availiblity_end, this.timeFormat);
    },
  },

  methods: {
    ...mapActions([
      'connect',
      'fetchBookings',
      'fetchSpaces',
      'fetchAlerts',
      'fetchCapAlertURLs',
      'setErrorMessage',
    ]),
    getScreenID() {
      // Captures screen ID in user agent string
      const captureID = new RegExp(/id=(.{12})/, 'ig');
      const userAgent = window.navigator.userAgent;
      const screenID = captureID.test(userAgent)
        ? userAgent
          .match(captureID)
          .join('')
          .slice(3)
        : null;
      // Expose screen ID to components
      this.screenID = screenID;
      return screenID;
    },
    checkScreenActivated(screen_id) {
      // Validates screen ID
      this.connect(screen_id)
        .then(() => {
          // Reset device inactive flag if screenID is valid
          this.screenIDInactive = false;
          const fetchedBookings = this.fetchBookings({
            date: this.selectedDate.format('YYYY-MM-DD'),
            spaceId: this.device.space_id,
          });
          const fetchedSpaces = this.fetchSpaces();

          Promise.all([fetchedBookings, fetchedSpaces])
            .then(() => {
              this.loading = false;

              if (navigator.onLine) {
                this.fetchCapAlertURLs().then(() => {
                  this.capAlertURLs.forEach(this.fetchAlerts);
                  this.checkCapAlertInterval = setInterval(() => {
                    this.capAlertURLs.forEach(this.fetchAlerts);
                  }, 600000);
                });

                if (!this.isOpenAvailability) {
                  this.checkSpaceAvailability();
                  this.checkAvailabilityInterval = setInterval(
                    this.checkSpaceAvailability,
                    5000,
                  );
                }

                // Poll booking availability on a 5 minute interval
                this.refreshEventsInterval = setInterval(() => {
                  this.fetchBookings({
                    date: this.selectedDate.format('YYYY-MM-DD'),
                    spaceId: this.spaceId,
                  });
                }, 300000);

                // Poll screen ID validation status on a 5 minute interval
                this.checkScreenIDInterval = setTimeout(() => {
                  this.screenID !== null
                    ? this.checkScreenActivated(this.screenID)
                    : this.checkScreenActivated(this.getScreenID());
                }, 300000);
              }
            });
        })
        .catch((error) => {
          if (error.response.status === 400) {
            // Display device activation screen if not validated
            this.screenIDInactive = true;
            // Poll screen ID validity every 1 minute
            setTimeout(() => {
              this.checkScreenActivated(this.getScreenID());
            }, 60000);
          }
        });
    },
    checkSpaceAvailability() {
      const currentTime = moment();
      this.showSlideshows = !currentTime.isBetween(
        this.availabilityStart,
        this.availabilityEnd,
      );
    },
  },

  watch: {
    showSlideshows(curr, prev) {
      // Sets BSHWLED to off
      // setBSHWLED('off')
      if (prev !== curr && curr === true) {
        getSlideshows().then((resp) => {
          this.slideshows = resp.data.result;

          let slides = resp.data.result.slides;

          if (!resp.data.result.randomize) {
            slides.sort(
              (currSlide, prevSlide) => currSlide.order - prevSlide.order,
            );
          } else {
            const shuffleArray = (array) => {
              // eslint-disable-next-line
              for (let i = array.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                // eslint-disable-next-line
                [array[i], array[j]] = [array[j], array[i]];
              }

              return array;
            };

            slides = shuffleArray(slides);
          }

          Vue.set(this.slideshows, 'slides', slides);
        });
      }
    },

    capAlerts(alert) {
      this.showModal(
        CapAlertModal,
        {
          ...alert,
          title: alert.event,
        },
        {
          height: '90%',
          classes: null,
        },
      );
    },

    selectedDate() {
      this.fetchBookings({
        date: this.selectedDate.format('YYYY-MM-DD'),
        spaceId: this.spaceId,
      });
    },
  },
};
</script>

<style lang="scss">
#app {
  height: 100vh;
}

div.header-container {
  height: 10%;
}

footer {
  > div {
    position: absolute;
    bottom: 0;
    width: 100%;
    clear: both;
  }
}

div.header-container {
  padding: 35px;
}

div.app-body {
  height: 80%;

  > div {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue